import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import { apiFetch } from '../lib/api';

/**
 * Persistent red banner shown whenever the current Firebase ID token
 * carries an `imp_by` custom claim — set when an admin minted the
 * token via /api/admin/impersonate/:userId/start. Reads the claim
 * client-side via getIdTokenResult and re-checks on token refresh.
 *
 * "Stop impersonating" calls /api/admin/impersonate/end (audit only)
 * and signs out the impersonation session. The admin's own session in
 * admin.lexcrm.aryeh.win is untouched (separate origin → separate
 * Firebase persistence).
 */
export default function ImpersonationBanner() {
  const { user, signOut } = useAuth();
  const [impBy, setImpBy] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setImpBy(null);
      return;
    }
    user
      .getIdTokenResult(false)
      .then((res) => {
        if (cancelled) return;
        const c = res.claims as { imp_by?: string };
        setImpBy(typeof c.imp_by === 'string' ? c.imp_by : null);
      })
      .catch(() => {
        if (!cancelled) setImpBy(null);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!user || !impBy) return null;

  async function stop() {
    setBusy(true);
    try {
      // Best-effort audit emit before signing out — once we sign out,
      // the bearer token is gone and the api can't see who ended it.
      await apiFetch('/api/admin/impersonate/end', { method: 'POST' }).catch(() => {});
      // We're piggy-backing on the same Firebase session; signing out
      // here ends the impersonation entirely. The admin stays signed
      // in on admin.lexcrm.aryeh.win because that's a different origin.
      await signOut();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="impersonation-banner" role="alert" aria-live="polite">
      <span className="impersonation-banner__icon">⚠️</span>
      <span className="impersonation-banner__text">
        Impersonating <strong>{user.email}</strong> · admin <code>{impBy.slice(0, 8)}</code>
      </span>
      <button type="button" onClick={stop} disabled={busy}>
        {busy ? 'Ending…' : 'Stop impersonating'}
      </button>
    </div>
  );
}
